/**
 * RecentCoachCommentsCard — Athlete Dashboard (B-2026-0020 Tier 2)
 *
 * Fetches GET /api/users/me/comments/recent?limit=5 and renders the last 5
 * comments coaches have left on the current athlete's evaluations.
 *
 * Each row deep-links to /evaluations/{evaluation_id}#comment-{comment_id}
 * so the evaluation page can scroll to the specific comment.
 *
 * Three states:
 * - Loading: skeleton rows matching the card skeleton pattern used across the dashboard.
 * - Empty: quiet "no comments yet" message.
 * - Populated: up to 5 rows with coach name, truncated comment excerpt, and relative time.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { getRecentCoachComments } from '@/services/commentApi';
import type { RecentCoachComment } from '@/services/commentApi';
import { formatRelativeTime } from './formatRelativeTime';

// ─── Constants ────────────────────────────────────────────────────────────────

const COMMENT_EXCERPT_MAX_CHARS = 120;
const DEFAULT_LIMIT = 5;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Truncate a string to maxLen characters, appending "…" when cut.
 * Matches the truncation pattern described in the ticket brief.
 */
function truncate(text: string, maxLen = COMMENT_EXCERPT_MAX_CHARS): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen).trimEnd() + '…';
}

// ─── Skeleton row ─────────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        padding: '12px 0',
        borderBottom: '1px solid var(--color-border-subtle)',
      }}
    >
      <div
        className="animate-pulse"
        style={{
          height: '12px',
          width: '30%',
          borderRadius: '4px',
          background: 'var(--color-border-subtle)',
        }}
      />
      <div
        className="animate-pulse"
        style={{
          height: '12px',
          width: '80%',
          borderRadius: '4px',
          background: 'var(--color-border-subtle)',
        }}
      />
    </div>
  );
}

// ─── Comment row ──────────────────────────────────────────────────────────────

function CommentRow({ comment }: { comment: RecentCoachComment }) {
  const href = `/evaluations/${comment.evaluation_id}#comment-${comment.comment_id}`;
  const excerpt = truncate(comment.comment_text);
  const relTime = formatRelativeTime(comment.created_at);

  return (
    <Link
      to={href}
      style={{ textDecoration: 'none', display: 'block' }}
    >
      <div
        style={{
          padding: '12px 14px',
          borderRadius: '10px',
          border: '1px solid var(--color-border-subtle)',
          backgroundColor: 'var(--color-dark-elevated-1)',
          transition: 'border-color 140ms ease, background-color 140ms ease',
          marginBottom: '8px',
          cursor: 'pointer',
        }}
        onMouseEnter={(e) => {
          const el = e.currentTarget as HTMLDivElement;
          el.style.borderColor = 'rgba(61, 169, 252, 0.35)';
          el.style.backgroundColor = 'rgba(61, 169, 252, 0.04)';
        }}
        onMouseLeave={(e) => {
          const el = e.currentTarget as HTMLDivElement;
          el.style.borderColor = 'var(--color-border-subtle)';
          el.style.backgroundColor = 'var(--color-dark-elevated-1)';
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: '4px',
            gap: '8px',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 600,
              fontSize: '12px',
              color: 'var(--color-accent-blue)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flexShrink: 0,
              maxWidth: '50%',
            }}
          >
            {comment.author_name}
          </span>
          <span
            style={{
              fontSize: '11px',
              color: 'var(--color-text-muted)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {relTime}
          </span>
        </div>
        <p
          style={{
            fontSize: '13px',
            color: 'var(--color-text-secondary)',
            lineHeight: 1.5,
            margin: 0,
          }}
        >
          {excerpt}
        </p>
      </div>
    </Link>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * Card showing the athlete's most recent coach comments across all their
 * evaluations. Placed on the AthleteDashboard.
 */
export function RecentCoachCommentsCard() {
  const { data: comments, isLoading, isError } = useQuery({
    queryKey: ['recent-coach-comments'],
    queryFn: () => getRecentCoachComments(DEFAULT_LIMIT),
    staleTime: 30_000,
  });

  return (
    <Card>
      <CardHeader style={{ paddingBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ChatBubbleLeftRightIcon
            style={{ width: '18px', height: '18px', color: 'var(--color-accent-blue)' }}
          />
          <CardTitle>Coach Comments</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div>
            {[0, 1, 2].map((i) => (
              <SkeletonRow key={i} />
            ))}
          </div>
        ) : isError ? (
          // Silent fail — matches existing dashboard error pattern
          <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', padding: '8px 0' }}>
            Could not load comments.
          </p>
        ) : !comments || comments.length === 0 ? (
          <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', padding: '8px 0' }}>
            No recent comments from coaches yet.
          </p>
        ) : (
          <div>
            {comments.map((comment) => (
              <CommentRow key={comment.comment_id} comment={comment} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
